import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { MarkerConfig } from '@shared/interfaces/marker-config';

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
  private map;
  private geocoder;
  private mapsModule;
  private geocodingModule;
  private markerModule;
  private googleLoadPromise;
  private placesModule;
  private mapClickSubscriptions = [];
  private markers = [];
  private cluster;

  constructor() {
    this.googleLoadPromise = this.loadService();
  }

  public async getAddress(latitude, longitude) {
    await this.googleLoadPromise;
    const location = {
      lat: Number(latitude),
      lng: Number(longitude),
    };

    return new Promise((resolve, reject) =>
      this.geocoder.geocode({ location }, (results, status) =>
        status === 'OK'
          ? resolve(results)
          : reject(`Geocoder failed due to ${status}`),
      ),
    );
  }

  public async getLatLng(address: string) {
    await this.googleLoadPromise;
    return new Promise((resolve, reject) =>
      this.geocoder.geocode({ address }, (results, status) =>
        status === 'OK'
          ? resolve(results)
          : reject(new Error(`Geocoder failed due to ${status}`)),
      ),
    );
  }

  public async initMap(latitude?, longitude?, mapElement?, disableDefaultUI = true) {
    await this.googleLoadPromise;
    this.map = await this.createMap(latitude, longitude, mapElement, disableDefaultUI);
    this.map.addListener('click', (event) => {
      this.mapClickSubscriptions.forEach((subscription) => subscription(event));
    });
  }

  private loadService() {
    const loader = new Loader({
      apiKey: 'AIzaSyBsu7zLSMrEB7tc377iC4D60Okod1yF1U0',
      version: 'weekly',
    });

    return Promise.all([
      loader.importLibrary('maps'),
      loader.importLibrary('routes'),
      loader.importLibrary('geocoding'),
      loader.importLibrary('marker'),
      loader.importLibrary('core'),
      loader.importLibrary('places'),
    ]).then(([maps, _routes, geocoding, marker, _core, places]) => {
      this.mapsModule = maps;
      this.geocodingModule = geocoding;
      this.markerModule = marker;
      this.placesModule = places;
      this.geocoder = new this.geocodingModule.Geocoder();
    });
  }

  private async createMap(latitude?, longitude?, mapElement?, disableDefaultUI = true) {
    const hasLocation = latitude !== undefined && longitude !== undefined;
    await this.googleLoadPromise;
    const map = new this.mapsModule.Map(mapElement || document.getElementById('map'), {
      center: hasLocation
        ? { lat: latitude, lng: longitude }
        : { lat: -15, lng: -47 },
      zoom: hasLocation ? 15 : 4,
      disableDefaultUI,
      mapTypeId: 'hybrid',
      mapId: 'b3ce77239e48d993',
    });

    const marker = await this.createNewMarker(map, { latitude, longitude, scale: 1.2 });
    if (marker) {
      this.markers.push(marker);
    }
    this.cluster = new MarkerClusterer({
      map,
      markers: this.markers,
    });
    return map;
  }

  public async createNewMarker(map, config: MarkerConfig) {
    if (!config.latitude || !config.longitude) {
      return;
    }
    await this.googleLoadPromise;

    const icon = this.createIcon(config);
    const pin = new this.markerModule.PinElement({
      glyph: icon,
      background: config.background,
      borderColor: config.borderColor,
      scale: config.scale,
    });
    const marker = new this.markerModule.AdvancedMarkerElement({
      position: { lat: config.latitude, lng: config.longitude },
      map,
      title: 'Local',
      draggable: false,
      content: pin.element,
    });

    marker.addListener('click', () => {
      map.panTo(marker.position);
      map.setZoom(20);
    });

    return marker;
  }

  private createIcon(config: MarkerConfig) {
    const hasPosition = config.latitude !== undefined && config.longitude !== undefined;
    if (!config.iconName || !hasPosition) {
      return;
    }
    const iconUrl = `assets/icon/${config.iconName}.svg`;
    const icon = document.createElement('div');
    icon.style.width = '100%';
    icon.style.height = '100%';
    icon.style.mask = `url(${iconUrl}) no-repeat center`;
    icon.style.maskSize = 'contain';
    icon.style.width = config.iconSize ? `${config.iconSize}px` : '30px';
    icon.style.height = config.iconSize ? `${config.iconSize}px` : '30px';
    icon.style.backgroundColor = config.iconColor || '#fff';
    icon.style.backgroundPosition = 'center';
    icon.style.borderRadius = '50%';
    icon.style.cursor = 'pointer';
    const iconWrapper = document.createElement('div');
    iconWrapper.style.position = 'relative';
    iconWrapper.appendChild(icon);

    return iconWrapper;
  }

  public async listPlacesByName(name: string) {
    await this.googleLoadPromise;
    return new Promise((resolve, reject) => {
      const autocompleteService = new this.placesModule.AutocompleteService();

      autocompleteService.getPlacePredictions({ input: name }, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const places = predictions.map(prediction => ({
            id: prediction.place_id,
            description: prediction.description,
          }));
          resolve(places);
        } else {
          reject(new Error(`Autocomplete failed due to ${status}`));
        }
      });
    });
  }

  public async getPlaceDetails(placeId: string) {
    await this.googleLoadPromise;
    return new Promise((resolve, reject) => {
      const placesService = new this.placesModule.PlacesService(this.map);
      placesService.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          reject(new Error(`Place details failed due to ${status}`));
        }
      });
    });
  }

  public showPlaceOnMap(placeId: string) {
    this.getPlaceDetails(placeId).then((place: any) => {
      this.map.setCenter(place.geometry.location);
      this.map.setZoom(15);
    });
  }

  public subscribeToMapClick(callback) {
    this.mapClickSubscriptions.push(callback);
  }

  public unsubscribeToMapClick() {
    this.mapClickSubscriptions = [];
  }

  async clearMarkers() {
    this.markers.forEach(marker => marker.setMap(null));
    this.cluster?.clearMarkers();
    this.markers = [];
  }

  public async addDefaultMarker(latitude: number, longitude: number) {
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      return;
    }
    const marker = await this.createNewMarker(this.map, { latitude, longitude, scale: 1.2 });
    this.cluster?.addMarker(marker);
    this.markers.push(marker);
  }

}
